import {
  deleteV1CompaniesCompanyIdMaterialTypesId,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdMaterialTypes,
  getV1CompaniesCompanyIdMaterials,
  getV1CompaniesCompanyIdMaterialTypeahead,
  getV1CompaniesCompanyIdMaterialTypes,
  GetV1CompaniesCompanyIdMaterialTypesData,
  getV1CompaniesCompanyIdOrdersOrderIdMaterialsTypeahead,
  Material_Create,
  MaterialState,
  patchV1CompaniesCompanyIdMaterialTypesId,
  postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdMaterialTypes,
  postV1CompaniesCompanyIdMaterials,
  postV1CompaniesCompanyIdMaterialTypes,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Material, MaterialType, MaterialTypeahead } from '~hooks/useMaterials/models';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface GetMaterialsByCompanyIdParams {
  companyId: string;
  cursor?: string;
  limit: number;
  link?: PaginationLink;
  searchQuery?: string;
  signal?: AbortSignal;
}

interface PostMaterialTypesByCompanyIdProps {
  companyId: string;
  material: Material;
}

interface CreateMaterialByNameParams {
  companyId: string;
  name: string;
}

interface CreateMaterialTypeByCompanyIdProps {
  companyId: string;
  managedCompanyId?: string;
  materialType: MaterialType;
}

interface GetMaterialTypesByCompanyIdProps {
  companyId: string;
  cursor?: string;
  limit?: number;
  link?: PaginationLink;
  managedCompanyId?: string;
  signal?: AbortSignal;
}

interface UpdateMaterialTypeProps {
  companyId: string;
  materialType: MaterialType;
}

interface DeleteMaterialTypeProps {
  companyId: string;
  materialTypeId: string;
}

interface GetMaterialsTypeaheadProps {
  companyId: string;
  cursor?: string;
  limit?: number;
  orderId?: string;
  query?: string;
}

export default function useMaterials() {
  const { companyAssetsStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const loadMaterialsIntoStore = useCallback(async () => {
    try {
      const response = await getV1CompaniesCompanyIdMaterials({
        path: { 'company-id': companyId },
        query: { 'page[limit]': MAX_API_PAGE_SIZE },
      });
      companyAssetsStore.setMaterials(response.data.data.map(Material.parse));
    } catch (error) {
      // callers fire and forget this load
      reportRequestError(error, t('error_messages.materials.failed_to_fetch'));
    }
  }, [companyAssetsStore, companyId]);

  const createMaterialByCompanyId = async ({
    companyId,
    material,
  }: PostMaterialTypesByCompanyIdProps) => {
    try {
      const response = await postV1CompaniesCompanyIdMaterials({
        path: { 'company-id': companyId },
        body: Material.deparse(material),
      });
      const createdMaterial = Material.parse(response.data.data);
      companyAssetsStore.addMaterial(createdMaterial);
      return createdMaterial;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.materials.failed_to_create'),
      );
      throw error;
    }
  };

  // Name-only create backing the "Add new" chip in form typeaheads. The body is
  // typed Material_Create directly so callers don't have to fake a Material
  // instance; state is pinned ACTIVE and material_type is omitted (the BE
  // defaults it to `Other`), matching the payload the legacy project form sent.
  const createMaterialByName = useCallback(
    async ({ companyId, name }: CreateMaterialByNameParams) => {
      try {
        const body: Material_Create = { name, state: MaterialState.ACTIVE };
        const response = await postV1CompaniesCompanyIdMaterials({
          path: { 'company-id': companyId },
          body,
        });
        const createdMaterial = Material.parse(response.data.data);
        companyAssetsStore.addMaterial(createdMaterial);

        return createdMaterial;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.materials.failed_to_create'),
        );
        throw error;
      }
    },
    [],
  );

  const getMaterialsByCompanyId = useCallback(
    async ({
      companyId,
      cursor,
      limit,
      link,
      searchQuery,
      signal,
    }: GetMaterialsByCompanyIdParams) => {
      try {
        const response = await getV1CompaniesCompanyIdMaterials({
          path: { 'company-id': companyId },
          query: {
            'page[limit]': limit,
            ...(searchQuery ? { 'search[datagrid]': searchQuery } : {}),
            ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          },
          signal,
        });

        return {
          data: response.data.data.map(Material.parse),
          pagination: extractPagination(response),
        };
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.materials.failed_to_fetch'),
        );
        // handleRequestError already throws; TS can't see that through the instance call
        throw error;
      }
    },
    [],
  );

  const getMaterialsByCompanyIdTypeahead = useCallback(
    async (options: GetMaterialsTypeaheadProps) => {
      try {
        const query = {
          'page[limit]': options?.limit || MAX_API_PAGE_SIZE,
          ...(options?.query ? { 'search[query]': options.query } : {}),
          ...(options?.cursor ? { 'page[after]': options.cursor } : {}),
        };
        // an order's own materials may belong to a different company than the
        // acting user's (e.g. a vendor-side user on a customer's order), so the
        // order-scoped endpoint is preferred whenever an order context exists
        const response = options.orderId
          ? await getV1CompaniesCompanyIdOrdersOrderIdMaterialsTypeahead({
              path: { 'company-id': options.companyId, 'order-id': options.orderId },
              query,
            })
          : await getV1CompaniesCompanyIdMaterialTypeahead({
              path: { 'company-id': options.companyId },
              query,
            });
        const materials = response.data.data.map(MaterialTypeahead.parse);

        return { data: materials, pagination: extractPagination(response) };
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.materials.failed_to_fetch'),
        );
      }
    },
    [],
  );

  const getMaterialTypesByCompanyId = useCallback(
    async ({
      companyId,
      cursor,
      limit = MAX_API_PAGE_SIZE,
      link,
      managedCompanyId,
      signal,
    }: GetMaterialTypesByCompanyIdProps) => {
      try {
        const query: NonNullable<GetV1CompaniesCompanyIdMaterialTypesData['query']> = {
          'page[limit]': limit,
          'sort[name]': 'asc',
          ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
        };

        const response = managedCompanyId
          ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdMaterialTypes({
              path: { 'company-id': companyId, 'managed-company-id': managedCompanyId },
              query,
              signal,
            })
          : await getV1CompaniesCompanyIdMaterialTypes({
              path: { 'company-id': companyId },
              query,
              signal,
            });

        return {
          data: response.data.data.map(MaterialType.parse),
          pagination: extractPagination(response),
        };
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.material_types.failed_to_fetch'),
        );
        throw error;
      }
    },
    [],
  );

  const createMaterialTypeByCompanyId = useCallback(
    async ({
      companyId,
      managedCompanyId,
      materialType,
    }: CreateMaterialTypeByCompanyIdProps) => {
      try {
        const body = MaterialType.deparse(materialType);
        const response = managedCompanyId
          ? await postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdMaterialTypes({
              path: { 'company-id': companyId, 'managed-company-id': managedCompanyId },
              body,
            })
          : await postV1CompaniesCompanyIdMaterialTypes({
              path: { 'company-id': companyId },
              body,
            });

        return MaterialType.parse(response.data.data);
      } catch (error) {
        // re-throw 422 errors so forms can show the invalid fields
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.material_types.failed_to_create'),
        );
        throw error;
      }
    },
    [],
  );

  const updateMaterialType = useCallback(
    async ({ companyId, materialType }: UpdateMaterialTypeProps) => {
      try {
        const body = MaterialType.deparseUpdate(materialType);
        const response = await patchV1CompaniesCompanyIdMaterialTypesId({
          path: { 'company-id': companyId, id: materialType.id },
          body,
        });
        return MaterialType.parse(response.data.data);
      } catch (error) {
        // re-throw 422 errors so forms can show the invalid fields
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.material_types.failed_to_update'),
        );
        throw error;
      }
    },
    [],
  );

  const deleteMaterialType = useCallback(
    async ({ companyId, materialTypeId }: DeleteMaterialTypeProps) => {
      try {
        await deleteV1CompaniesCompanyIdMaterialTypesId({
          path: { 'company-id': companyId, id: materialTypeId },
        });
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.material_types.failed_to_delete'),
        );
        // re-throw so the caller doesn't treat a failed delete as success
        throw error;
      }
    },
    [],
  );

  return {
    createMaterialByCompanyId,
    createMaterialByName,
    createMaterialTypeByCompanyId,
    deleteMaterialType,
    getMaterialsByCompanyId,
    getMaterialsByCompanyIdTypeahead,
    getMaterialTypesByCompanyId,
    loadMaterialsIntoStore,
    updateMaterialType,
  };
}
